import React, { useMemo } from 'react';
import './PatrimoniaPopup.css';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '−') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function fmtEur2(value) {
  if (value == null || isNaN(value)) return '—';
  return `€${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function PatrimoniaPopup({ onClose, investment }) {
  const d = investment?.patrimoniaDetail || {};

  const derived = useMemo(() => {
    const shares = d.shares_held ?? null;
    const nav = d.nav_per_share_eur ?? null;
    const navPrev = d.nav_per_share_prev_eur ?? null;
    const cost = d.cost_basis_eur ?? (d.cost_per_share_eur != null && shares != null ? d.cost_per_share_eur * shares : null);
    const value = (shares != null && nav != null) ? shares * nav : (investment?.eurValue ?? null);
    const gain = (value != null && cost != null) ? value - cost : null;
    const gainPct = (gain != null && cost) ? (gain / cost) * 100 : null;
    const navYoYPct = (nav != null && navPrev) ? ((nav - navPrev) / navPrev) * 100 : null;
    const stakePct = (shares != null && d.shares_total) ? (shares / d.shares_total) * 100 : null;
    return { shares, nav, navPrev, cost, value, gain, gainPct, navYoYPct, stakePct };
  }, [d, investment]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content patrim-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="patrim-header">
          <h2>Patrimonia — {d.company || 'Patrimmonia Real Estate NV'}</h2>
          <div className="popup-header-actions">
            <SeeFilesButton url={getDriveFolderUrl('Patrimonia')} />
            <button className="patrim-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* KPI row */}
        <div className="patrim-kpis">
          <div className="patrim-kpi">
            <span className="patrim-label">Current value</span>
            <span className="patrim-big">{fmtEur(derived.value)}</span>
            <span className="patrim-sub">
              {derived.shares != null ? `${derived.shares} shares` : ''}
              {d.shares_total ? ` / ${d.shares_total.toLocaleString()}` : ''}
              {derived.stakePct != null ? ` · ${derived.stakePct.toFixed(2)}%` : ''}
            </span>
          </div>
          <div className="patrim-kpi">
            <span className="patrim-label">vs cost</span>
            <span className={`patrim-mid ${derived.gain >= 0 ? 'patrim-pos' : 'patrim-neg'}`}>
              {fmtEur(derived.gain, true)}
            </span>
            <span className="patrim-sub">{derived.gainPct != null ? `${fmtPct(derived.gainPct)} on ${fmtEur(derived.cost)}` : ''}</span>
          </div>
          <div className="patrim-kpi">
            <span className="patrim-label">NAV / share</span>
            <span className="patrim-mid">{fmtEur2(derived.nav)}</span>
            <span className="patrim-sub">
              {derived.navPrev != null ? `was ${fmtEur2(derived.navPrev)}` : ''}
              {derived.navYoYPct != null ? ` · ${fmtPct(derived.navYoYPct)} YoY` : ''}
            </span>
          </div>
        </div>

        {/* Facts */}
        <div className="patrim-facts">
          <div className="patrim-row"><span>Cost basis</span><strong>{fmtEur(derived.cost)}{d.cost_per_share_eur ? ` (€${d.cost_per_share_eur.toLocaleString()}/sh)` : ''}</strong></div>
          <div className="patrim-row"><span>NAV source</span><strong>{d.nav_source || '—'}</strong></div>
          <div className="patrim-row"><span>NAV date</span><strong>{formatDateLong(d.nav_date)}</strong></div>
          {d.group_nav_eur != null && (
            <div className="patrim-row"><span>Group NAV (100%)</span><strong>{fmtEur(d.group_nav_eur)}</strong></div>
          )}
          {d.book_equity_eur != null && (
            <div className="patrim-row"><span>Statutory book equity</span><strong>{fmtEur(d.book_equity_eur)}</strong></div>
          )}
          {d.structure && (
            <div className="patrim-row patrim-row--wrap"><span>Structure</span><strong>{d.structure}</strong></div>
          )}
        </div>

        {d.liquidity_note && (
          <div className="patrim-note">
            <span className="patrim-note-tag">Liquidity</span> {d.liquidity_note}
          </div>
        )}

        <div className="patrim-foot">
          Valued at the company's annual sum-of-the-parts NAV. Updated yearly from the June AGM presentation.
          {d.as_of ? ` Last updated ${formatDateLong(d.as_of)}.` : ''}
        </div>
      </div>
    </div>
  );
}

export default PatrimoniaPopup;
